import type { StaffCatalogItemDetail, StaffCatalogItemPage } from "@lafayette/schemas";
import { useDataProvider } from "@refinedev/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { catalogResources } from "./data-provider";
import { CATALOG_PAGE_SIZE, type CatalogFilterQuery } from "./filters";

// Reads. Every read goes through the Refine data provider (`useDataProvider`) and is cached by
// TanStack Query (the query client Refine owns). A command writes the detail it gets back into the same
// cache entry, so the page shows the new version without a second request.

export const catalogKeys = {
  all: ["catalog"] as const,
  list: (query: CatalogFilterQuery) => ["catalog", "list", query] as const,
  item: (itemId: string) => ["catalog", "item", itemId] as const,
};

export function useItemFetcher() {
  const getDataProvider = useDataProvider();
  return useCallback(
    async (itemId: string, signal?: AbortSignal): Promise<StaffCatalogItemDetail> => {
      const response = await getDataProvider().getOne({
        resource: catalogResources.items,
        id: itemId,
        ...(signal === undefined ? {} : { meta: { signal } }),
      });
      return response.data as StaffCatalogItemDetail;
    },
    [getDataProvider],
  );
}

export function useCatalogItem(itemId: string, enabled = true) {
  const fetchItem = useItemFetcher();
  return useQuery({
    queryKey: catalogKeys.item(itemId),
    queryFn: ({ signal }) => fetchItem(itemId, signal),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/** The list, one keyset page at a time. Each page passes the previous page's cursor in `meta.cursor`. */
export function useCatalogList(filters: CatalogFilterQuery, enabled = true) {
  const getDataProvider = useDataProvider();
  return useInfiniteQuery({
    queryKey: catalogKeys.list(filters),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }): Promise<StaffCatalogItemPage> => {
      const response = await getDataProvider().getList({
        resource: catalogResources.items,
        meta: {
          query: filters,
          cursor: pageParam,
          limit: CATALOG_PAGE_SIZE,
          signal,
        },
      });
      return {
        items: response.data as StaffCatalogItemPage["items"],
        nextCursor: (response as unknown as { nextCursor: string | null }).nextCursor,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
